import React from 'react';
import {
	X,
	CheckCircle,
	XCircle,
	AlertTriangle,
	Camera,
	Eye,
	Users,
	Lightbulb,
} from 'lucide-react';

interface FacialVerificationGuideProps {
	isOpen: boolean;
	onClose: () => void;
}

const FacialVerificationGuide: React.FC<FacialVerificationGuideProps> = ({ isOpen, onClose }) => {
	if (!isOpen) return null;

	const guidelines = [
	  {
	    id: 1,
	    title: 'Face is too close',
	    icon: <XCircle className="w-6 h-6 text-red-500" />,
	    problem: 'Face is too close to the camera or image is cropped too small',
	    solution: 'Keep camera at arm\'s length distance (about 1 meter away)',
	    image:
	      'https://images.pexels.com/photos/1181686/pexels-photo-1181686.jpeg?auto=compress&cs=tinysrgb&w=300&h=200&fit=crop',
	  },
	  {
	    id: 2,
	    title: 'Face is partially covered',
	    icon: <XCircle className="w-6 h-6 text-red-500" />,
	    problem: 'Part of face is covered by mask, hand, or other objects',
	    solution: 'Ensure all facial features are visible: ears, eyes, chin, lips, nose, and hair',
	    image:
	      'https://images.pexels.com/photos/1040880/pexels-photo-1040880.jpeg?auto=compress&cs=tinysrgb&w=300&h=200&fit=crop',
	  },
	  {
	    id: 3,
	    title: 'Failed to detect face',
	    icon: <XCircle className="w-6 h-6 text-red-500" />,
	    problem: 'Poor lighting, motion blur, or face not facing camera',
	    solution: 'Ensure adequate lighting and face the camera directly',
	    image:
	      'https://images.pexels.com/photos/3952048/pexels-photo-3952048.jpeg?auto=compress&cs=tinysrgb&w=300&h=200&fit=crop',
	  },
	  {
	    id: 4,
	    title: 'Face is too small',
	    icon: <XCircle className="w-6 h-6 text-red-500" />,
	    problem: 'Image captured from too far away',
	    solution: 'Stand closer to camera, within 1 meter distance',
	    image:
	      'https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=300&h=200&fit=crop',
	  },
	  {
	    id: 5,
	    title: 'Too many faces detected',
	    icon: <Users className="w-6 h-6 text-red-500" />,
	    problem: 'Multiple faces present in the image',
	    solution: 'Ensure only one face is visible in the frame',
	    image:
	      'https://images.pexels.com/photos/1181424/pexels-photo-1181424.jpeg?auto=compress&cs=tinysrgb&w=300&h=200&fit=crop',
	  },
	];

	return (
	  <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
	    <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto scrollbar-none">
	      <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-1 rounded-t-2xl">
	        <div className="flex items-center justify-between py-4">
	          <div className="flex items-center gap-3">
	            <div className="bg-ghana-red rounded-full flex items-center justify-center p-4">
	              <Camera className="w-6 h-6 text-white" />
	            </div>
	            <div>
	              <h2 className="text-xl font-bold text-gray-900">Facial Verification Guide</h2>
	              <p className="text-gray-600 text-body">
	                Learn how to capture the perfect selfie for verification
	              </p>
	            </div>
	          </div>
	          <button
	            onClick={onClose}
	            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
	          >
	            <X className="w-6 h-6 text-gray-500" />
	          </button>
	        </div>
	      </div>

	      <div className="p-6">
	        {/* Correct Example First */}
	        <div className="mb-8 bg-green-50 border border-green-200 rounded-xl p-6">
	          <div className="flex items-center gap-3 mb-4">
	            <CheckCircle className="w-8 h-8 text-green-600" />
	            <h3 className="text-xl font-bold text-green-800">Correct Face Capture</h3>
	          </div>
	          <div className="grid md:grid-cols-2 gap-6 items-center">
	            <div>
	              <img
	                src="https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop"
	                alt="Correct face capture example"
	                className="w-full h-48 object-cover rounded-lg border-2 border-green-300"
	              />
	            </div>
	            <div className="space-y-3">
	              <h4 className="font-semibold text-green-800">Perfect Selfie Requirements:</h4>
	              <ul className="space-y-2 text-green-700">
	                <li className="flex items-center gap-2">
	                  <CheckCircle className="w-4 h-4" />
	                  Passport photo format and quality
	                </li>
	                <li className="flex items-center gap-2">
	                  <CheckCircle className="w-4 h-4" />
	                  Camera at arm's length distance
	                </li>
	                <li className="flex items-center gap-2">
	                  <CheckCircle className="w-4 h-4" />
	                  Camera positioned at face level
	                </li>
	                <li className="flex items-center gap-2">
	                  <CheckCircle className="w-4 h-4" />
	                  Well-lit environment
	                </li>
	                <li className="flex items-center gap-2">
	                  <CheckCircle className="w-4 h-4" />
	                  Live image, not a photograph
	                </li>
	              </ul>
	            </div>
	          </div>
	        </div>

	        {/* Common Mistakes */}
	        <div className="mb-8">
	          <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
	            <AlertTriangle className="w-6 h-6 text-amber-500" />
	            Common Mistakes to Avoid
	          </h3>
	          <div className="grid gap-6">
	            {guidelines.map(guideline => (
	              <div key={guideline.id} className="bg-red-50 border border-red-200 rounded-xl p-6">
	                <div className="grid md:grid-cols-3 gap-6">
	                  <div className="md:col-span-1">
	                    <img
	                      src={guideline.image}
	                      alt={`Example of ${guideline.title}`}
	                      className="w-full h-32 object-cover rounded-lg border-2 border-red-300"
	                    />
	                  </div>
	                  <div className="md:col-span-2">
	                    <div className="flex items-center gap-2 mb-3">
	                      {guideline.icon}
	                      <h4 className="text-lg font-semibold text-red-800">{guideline.title}</h4>
	                    </div>
	                    <div className="space-y-2">
	                      <div>
	                        <span className="text-sm font-medium text-red-700">Problem: </span>
	                        <span className="text-sm text-red-600">{guideline.problem}</span>
	                      </div>
	                      <div>
	                        <span className="text-sm font-medium text-red-700">Solution: </span>
	                        <span className="text-sm text-red-600">{guideline.solution}</span>
	                      </div>
	                    </div>
	                  </div>
	                </div>
	              </div>
	            ))}
	          </div>
	        </div>

	        {/* Technical Requirements */}
	        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
	          <h3 className="text-lg font-bold text-blue-800 mb-4 flex items-center gap-2">
	            <Eye className="w-5 h-5" />
	            Technical Requirements
	          </h3>
	          <div className="grid md:grid-cols-2 gap-6">
	            <div>
	              <h4 className="font-semibold text-blue-700 mb-2">Image Specifications:</h4>
	              <ul className="space-y-1 text-blue-600 text-sm">
	                <li>• Recommended dimensions: 640 x 480 pixels</li>
	                <li>• Maximum file size: 1MB</li>
	                <li>• Format: PNG preferred</li>
	                <li>• Live capture only (no photographs of photos)</li>
	              </ul>
	            </div>
	            <div>
	              <h4 className="font-semibold text-blue-700 mb-2">Environment Setup:</h4>
	              <ul className="space-y-1 text-blue-600 text-sm">
	                <li>• Adequate lighting (avoid shadows)</li>
	                <li>• Neutral background preferred</li>
	                <li>• Stable camera position</li>
	                <li>• No motion blur</li>
	              </ul>
	            </div>
	          </div>
	        </div>

	        {/* Pro Tips */}
	        <div className="bg-ghana-gold bg-opacity-10 border border-ghana-gold rounded-xl p-6">
	          <h3 className="text-lg font-bold text-ghana-red mb-4 flex items-center gap-2">
	            <Lightbulb className="w-5 h-5" />
	            Pro Tips for Best Results
	          </h3>
	          <div className="grid md:grid-cols-2 gap-4">
	            <div className="space-y-2">
	              <div className="flex items-start gap-2">
	                <CheckCircle className="w-4 h-4 text-ghana-green mt-0.5 flex-shrink-0" />
	                <span className="text-sm text-gray-700">Look directly at the camera lens</span>
	              </div>
	              <div className="flex items-start gap-2">
	                <CheckCircle className="w-4 h-4 text-ghana-green mt-0.5 flex-shrink-0" />
	                <span className="text-sm text-gray-700">Keep a neutral facial expression</span>
	              </div>
	              <div className="flex items-start gap-2">
	                <CheckCircle className="w-4 h-4 text-ghana-green mt-0.5 flex-shrink-0" />
	                <span className="text-sm text-gray-700">Remove glasses if they cause glare</span>
	              </div>
	            </div>
	            <div className="space-y-2">
	              <div className="flex items-start gap-2">
	                <CheckCircle className="w-4 h-4 text-ghana-green mt-0.5 flex-shrink-0" />
	                <span className="text-sm text-gray-700">Ensure hair doesn't cover face</span>
	              </div>
	              <div className="flex items-start gap-2">
	                <CheckCircle className="w-4 h-4 text-ghana-green mt-0.5 flex-shrink-0" />
	                <span className="text-sm text-gray-700">Hold camera steady to avoid blur</span>
	              </div>
	              <div className="flex items-start gap-2">
	                <CheckCircle className="w-4 h-4 text-ghana-green mt-0.5 flex-shrink-0" />
	                <span className="text-sm text-gray-700">Take multiple shots if needed</span>
	              </div>
	            </div>
	          </div>
	        </div>
	      </div>

	      {/* Footer */}
	      <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 rounded-b-2xl">
	        <div className="flex justify-between items-center">
	          <p className="text-sm text-gray-600">
	            Following these guidelines ensures accurate verification results
	          </p>
	          <button
	            onClick={onClose}
	            className="px-6 py-2 bg-ghana-red text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap"
	          >
	            Got it!
	          </button>
	        </div>
	      </div>
	    </div>
	  </div>
	);
};

export default FacialVerificationGuide;
